import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

export type ProximityAxis = 'x' | 'y' | 'xy';

export type ProximityRect = {
  left: number;
  top: number;
  width: number;
  height: number;
};

export type UseProximityHoverOptions = {
  axis?: ProximityAxis;
  /** When false, hook is inert (no listeners / highlight). Default true. */
  enabled?: boolean;
  /** Targets inside the container. Default `[data-wp-proximity-item]`. */
  itemSelector?: string;
};

export type UseProximityHoverResult<T extends HTMLElement = HTMLElement> = {
  containerRef: RefObject<T | null>;
  containerProps: {
    onPointerMove: (e: ReactPointerEvent<T>) => void;
    onPointerLeave: (e: ReactPointerEvent<T>) => void;
    onPointerCancel: (e: ReactPointerEvent<T>) => void;
  };
  highlightStyle: CSSProperties;
  active: boolean;
  clear: () => void;
};

const DEFAULT_SELECTOR = '[data-wp-proximity-item]';

function finePointerHover(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.matchMedia('(hover: hover) and (pointer: fine)').matches
  );
}

function distanceToItem(axis: ProximityAxis, px: number, py: number, r: DOMRect): number {
  const cx = r.left + r.width / 2;
  const cy = r.top + r.height / 2;
  if (axis === 'x') {
    const clampedY = Math.min(Math.max(py, r.top), r.bottom);
    const dy = py - clampedY;
    return Math.abs(px - cx) + Math.abs(dy) * 0.25;
  }
  if (axis === 'y') {
    const clampedX = Math.min(Math.max(px, r.left), r.right);
    const dx = px - clampedX;
    return Math.abs(py - cy) + Math.abs(dx) * 0.25;
  }
  const dx = px < r.left ? r.left - px : px > r.right ? px - r.right : 0;
  const dy = py < r.top ? r.top - py : py > r.bottom ? py - r.bottom : 0;
  return Math.hypot(dx, dy);
}

/**
 * Track nearest interactive target under the pointer and expose a shared highlight rect.
 * Motion-as-information: wash precedes click; reduced-motion still shows highlight without chase
 * (CSS snaps transform when `--wp-spring-*` durations are 0).
 */
export function useProximityHover<T extends HTMLElement = HTMLElement>(
  options: UseProximityHoverOptions = {},
): UseProximityHoverResult<T> {
  const { axis = 'xy', enabled = true, itemSelector = DEFAULT_SELECTOR } = options;
  const containerRef = useRef<T | null>(null);
  const [rect, setRect] = useState<ProximityRect | null>(null);
  const [active, setActive] = useState(false);
  const rafRef = useRef<number | null>(null);
  const pendingRef = useRef<{ x: number; y: number } | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const clear = useCallback(() => {
    pendingRef.current = null;
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    setActive(false);
    setRect(null);
  }, []);

  const resolve = useCallback(() => {
    rafRef.current = null;
    const pending = pendingRef.current;
    const container = containerRef.current;
    if (!pending || !container || !enabledRef.current) return;
    if (!finePointerHover()) {
      clear();
      return;
    }

    const items = Array.from(container.querySelectorAll<HTMLElement>(itemSelector)).filter((el) => {
      if (el.closest('[aria-disabled="true"], [disabled]')) return false;
      if (el.getAttribute('data-wp-proximity-item') === 'false') return false;
      const style = window.getComputedStyle(el);
      if (style.pointerEvents === 'none' || style.visibility === 'hidden') return false;
      return el.getClientRects().length > 0;
    });
    if (items.length === 0) {
      clear();
      return;
    }

    const { x, y } = pending;
    let best: HTMLElement | null = null;
    let bestDist = Infinity;
    for (const el of items) {
      const r = el.getBoundingClientRect();
      const d = distanceToItem(axis, x, y, r);
      if (d < bestDist) {
        bestDist = d;
        best = el;
      }
    }
    if (!best) {
      clear();
      return;
    }

    const cr = container.getBoundingClientRect();
    const br = best.getBoundingClientRect();
    const next: ProximityRect = {
      left: Math.round(br.left - cr.left + container.scrollLeft),
      top: Math.round(br.top - cr.top + container.scrollTop),
      width: Math.round(br.width),
      height: Math.round(br.height),
    };
    setRect((prev) =>
      prev &&
      prev.left === next.left &&
      prev.top === next.top &&
      prev.width === next.width &&
      prev.height === next.height
        ? prev
        : next,
    );
    setActive(true);
  }, [axis, clear, itemSelector]);

  const schedule = useCallback(
    (x: number, y: number) => {
      if (!enabledRef.current) return;
      pendingRef.current = { x, y };
      if (rafRef.current != null) return;
      rafRef.current = requestAnimationFrame(resolve);
    },
    [resolve],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<T>) => {
      if (e.pointerType === 'touch') return;
      schedule(e.clientX, e.clientY);
    },
    [schedule],
  );

  const onPointerLeave = useCallback(() => {
    clear();
  }, [clear]);

  useEffect(() => {
    if (!enabled) clear();
  }, [enabled, clear]);

  useEffect(() => () => clear(), [clear]);

  const highlightStyle: CSSProperties = rect
    ? {
        transform: `translate3d(${rect.left}px, ${rect.top}px, 0)`,
        width: rect.width,
        height: rect.height,
        opacity: active ? 1 : 0,
      }
    : {
        opacity: 0,
        width: 0,
        height: 0,
        transform: 'translate3d(0, 0, 0)',
      };

  return {
    containerRef,
    containerProps: {
      onPointerMove,
      onPointerLeave,
      onPointerCancel: onPointerLeave,
    },
    highlightStyle,
    active,
    clear,
  };
}
